import { supabase } from '@/integrations/supabase/client';
import {
  EXPORTS_FOLDER,
  EXPORT_STORAGE_LIMIT_BYTES,
  SaveUserExportParams,
  SaveUserExportResult,
  UserExportRecord,
} from '@/constants/userExports';
import {
  addLocalExport,
  deleteLocalExport,
  getLocalExportBlob,
  getLocalExportUsedBytes,
  listLocalExports,
} from '@/utils/localExportsDb';

function sanitizeFileName(name: string): string {
  return name.replace(/[^\w.\-() ]+/g, '_').replace(/\s+/g, '-').slice(0, 120);
}

function getExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : 'bin';
}

function buildStoragePath(fileName: string): string {
  const safe = sanitizeFileName(fileName);
  const ext = getExtension(safe);
  const base = safe.replace(/\.[^.]+$/, '') || 'export';
  return `${EXPORTS_FOLDER}/${Date.now()}-${base}.${ext}`;
}

function inferFileType(fileName: string, fallback = 'Fichier'): string {
  const ext = getExtension(fileName).toUpperCase();
  const map: Record<string, string> = {
    pdf: 'PDF',
    png: 'PNG',
    jpg: 'JPEG',
    jpeg: 'JPEG',
    csv: 'CSV',
    xlsx: 'XLSX',
    json: 'JSON',
    txt: 'TXT',
    webp: 'WEBP',
  };
  return map[ext.toLowerCase()] || ext || fallback;
}

async function listCloudExports(userId: string): Promise<UserExportRecord[]> {
  const { data, error } = await supabase.storage
    .from('private-files')
    .list(`${userId}/${EXPORTS_FOLDER}`, {
      limit: 100,
      sortBy: { column: 'created_at', order: 'desc' },
    });

  if (error) {
    const message = error.message?.toLowerCase() ?? '';
    if (message.includes('not found') || message.includes('does not exist')) {
      return [];
    }
    throw error;
  }

  return (data || [])
    .filter((f) => f.name && !f.name.startsWith('.'))
    .map((file) => ({
      id: file.id || file.name,
      name:
        (file.metadata as { originalName?: string } | undefined)?.originalName || file.name,
      fileType: inferFileType(
        (file.metadata as { originalName?: string } | undefined)?.originalName || file.name
      ),
      sizeBytes: (() => {
        const meta = file.metadata as { size?: number | string } | undefined;
        if (typeof meta?.size === 'number') return meta.size;
        if (typeof meta?.size === 'string') {
          const parsed = Number(meta.size);
          return Number.isFinite(parsed) ? parsed : 0;
        }
        return 0;
      })(),
      createdAt: file.created_at || file.updated_at || new Date().toISOString(),
      storageKey: `${EXPORTS_FOLDER}/${file.name}`,
      sourceTool: (file.metadata as { sourceTool?: string } | undefined)?.sourceTool,
      isLocal: false,
    }));
}

async function getCloudUsedBytes(userId: string): Promise<number> {
  const items = await listCloudExports(userId);
  return items.reduce((sum, item) => sum + item.sizeBytes, 0);
}

export async function listUserExports(userId: string | null): Promise<UserExportRecord[]> {
  if (userId) return listCloudExports(userId);
  return listLocalExports();
}

export async function getUserExportsUsedBytes(userId: string | null): Promise<number> {
  if (userId) return getCloudUsedBytes(userId);
  return getLocalExportUsedBytes();
}

export async function saveUserExport(
  userId: string | null,
  params: SaveUserExportParams
): Promise<SaveUserExportResult> {
  const fileType = params.fileType || inferFileType(params.fileName);

  if (params.blob.size > EXPORT_STORAGE_LIMIT_BYTES) {
    return { ok: false, error: 'Ce fichier dépasse la limite de 1 Mo par export.' };
  }

  try {
    const used = await getUserExportsUsedBytes(userId);
    if (used + params.blob.size > EXPORT_STORAGE_LIMIT_BYTES) {
      return {
        ok: false,
        error: 'Quota de 1 Mo atteint. Libérez de l\'espace dans Mes Exports.',
      };
    }

    if (!userId) {
      const record = await addLocalExport({
        blob: params.blob,
        fileName: params.fileName,
        fileType,
        sourceTool: params.sourceTool,
      });
      return { ok: true, record };
    }

    const storagePath = buildStoragePath(params.fileName);
    const fullPath = `${userId}/${storagePath}`;
    const file = new File([params.blob], params.fileName, {
      type: params.blob.type || 'application/octet-stream',
    });

    const { error } = await supabase.storage
      .from('private-files')
      .upload(fullPath, file, {
        upsert: false,
        metadata: {
          originalName: params.fileName,
          sourceTool: params.sourceTool || '',
          size: String(params.blob.size),
        },
      });

    if (error) {
      return { ok: false, error: 'Impossible d\'enregistrer l\'export. Réessayez.' };
    }

    const record: UserExportRecord = {
      id: storagePath,
      name: params.fileName,
      fileType,
      sizeBytes: params.blob.size,
      createdAt: new Date().toISOString(),
      storageKey: storagePath,
      sourceTool: params.sourceTool,
      isLocal: false,
    };

    return { ok: true, record };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement.',
    };
  }
}

export async function downloadUserExport(
  userId: string | null,
  item: UserExportRecord
): Promise<void> {
  if (item.isLocal || !userId) {
    const blob = await getLocalExportBlob(item.storageKey);
    if (!blob) throw new Error('Fichier introuvable.');
    triggerBlobDownload(blob, item.name);
    return;
  }

  const fullPath = `${userId}/${item.storageKey}`;
  const { data, error } = await supabase.storage
    .from('private-files')
    .download(fullPath);

  if (error || !data) throw new Error('Téléchargement impossible.');

  triggerBlobDownload(data, item.name);
}

export async function deleteUserExport(
  userId: string | null,
  item: UserExportRecord
): Promise<void> {
  if (item.isLocal || !userId) {
    await deleteLocalExport(item.storageKey);
    return;
  }

  const fullPath = `${userId}/${item.storageKey}`;
  const { error } = await supabase.storage.from('private-files').remove([fullPath]);
  if (error) throw error;
}

export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function formatExportSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < EXPORT_STORAGE_LIMIT_BYTES) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
}

export function formatExportDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}
